// =============================================================================
// Shop filters.
//
// The sidebar is a real GET form rendered by PHP, so it filters with JavaScript
// switched off. This module only removes the round trip: any control change is
// serialised into the filter contract, fetched from the render endpoint, and
// the swapped regions come back as HTML drawn by the same PHP that drew the
// page. The address bar is kept in step the whole time, so every filtered view
// is a link that can be shared, bookmarked and returned to with the back
// button.
// =============================================================================

import { claimOverlay, releaseOverlay, onDismiss } from './overlay.js';

const settings = window.chengHua || {};

/**
 * Read every control of the sidebar form — including the sort select, which
 * sits in the toolbar but belongs to the form through its `form` attribute.
 *
 * @param {HTMLFormElement} form The sidebar form.
 * @return {Object} Filter state in the shape the endpoint speaks.
 */
function readState(form) {
	const state = {
		search: '',
		searchName: 'search',
		category: [],
		min_price: '',
		max_price: '',
		attributes: {},
		stock_status: '',
		sort: 'default',
	};

	for (const [key, value] of new FormData(form).entries()) {
		const text = String(value);

		if (key === 'search' || key === 's') {
			state.search = text;
			state.searchName = key;
		} else if (key === 'category[]') {
			state.category.push(text);
		} else if (key === 'min_price' || key === 'max_price') {
			state[key] = text;
		} else if (key === 'stock_status') {
			state.stock_status = text;
		} else if (key === 'ch_sort') {
			state.sort = text;
		} else if (key.startsWith('attributes[') && key.endsWith(']')) {
			const taxonomy = key.slice('attributes['.length, -1);

			(state.attributes[taxonomy] ||= []).push(text);
		}
	}

	return state;
}

/**
 * Write a state back into the form, for back/forward navigation and for chips
 * removing themselves.
 *
 * @param {HTMLFormElement} form  The sidebar form.
 * @param {Object}          state Filter state.
 */
function writeState(form, state) {
	// The sort select joins the form through its `form` attribute rather than
	// by nesting inside it, so form.querySelectorAll alone never reaches it.
	const fields = new Set(form.querySelectorAll('input, select'));

	document.querySelectorAll(`[form="${form.id}"]`).forEach((field) => fields.add(field));

	fields.forEach((field) => {
		const name = field.name;

		if (name === 'search' || name === 's') {
			field.value = state.search;
		} else if (name === 'category[]') {
			field.checked = state.category.includes(field.value);
		} else if (name === 'min_price' || name === 'max_price') {
			field.value = state[name];
		} else if (name === 'stock_status') {
			field.checked = field.value === state.stock_status;
		} else if (name === 'ch_sort') {
			field.value = state.sort;
		} else if (name.startsWith('attributes[')) {
			const taxonomy = name.slice('attributes['.length, -1);

			field.checked = (state.attributes[taxonomy] || []).includes(field.value);
		}
	});
}

/**
 * The state as endpoint parameters.
 */
function toApiParams(state, page, perPage, base) {
	const params = new URLSearchParams();

	if (state.search) params.set('search', state.search);
	if (state.category.length) params.set('category', state.category.join(','));
	if (state.min_price !== '') params.set('min_price', state.min_price);
	if (state.max_price !== '') params.set('max_price', state.max_price);
	if (state.stock_status) params.set('stock_status', state.stock_status);
	if (state.sort !== 'default') params.set('ch_sort', state.sort);

	const groups = Object.entries(state.attributes)
		.filter(([, terms]) => terms.length)
		.map(([taxonomy, terms]) => `${taxonomy}:${terms.join(',')}`);

	if (groups.length) params.set('attributes', groups.join('|'));

	params.set('ch_page', String(page));
	params.set('per_page', String(perPage));
	params.set('base', base);

	return params;
}

/**
 * The state as a shareable address: filters in the query string, the page in
 * the /page/N/ path segment the no-JavaScript pager uses.
 */
function toUrl(state, page, basePath) {
	const query = new URLSearchParams();

	if (state.search) query.set(state.searchName, state.search);
	if (state.category.length) query.set('category', state.category.join(','));
	if (state.min_price !== '') query.set('min_price', state.min_price);
	if (state.max_price !== '') query.set('max_price', state.max_price);
	if (state.stock_status) query.set('stock_status', state.stock_status);
	if (state.sort !== 'default') query.set('ch_sort', state.sort);

	const groups = Object.entries(state.attributes)
		.filter(([, terms]) => terms.length)
		.map(([taxonomy, terms]) => `${taxonomy}:${terms.join(',')}`);

	if (groups.length) query.set('attributes', groups.join('|'));

	const path = basePath + (page > 1 ? `page/${page}/` : '');
	const search = query.toString();

	return path + (search ? `?${search}` : '');
}

/**
 * The page an address points at, from either spelling.
 */
function pageFromLocation() {
	const inPath = window.location.pathname.match(/\/page\/(\d+)\/?$/);

	if (inPath) {
		return Math.max(1, parseInt(inPath[1], 10));
	}

	const param = new URLSearchParams(window.location.search).get('ch_page')
		|| new URLSearchParams(window.location.search).get('paged');

	return Math.max(1, parseInt(param || '1', 10));
}

/**
 * Rebuild a state from the address bar, for back/forward navigation.
 */
function stateFromLocation(form) {
	const params = new URLSearchParams(window.location.search);
	const state = readState(form);

	state.search = params.get('search') || params.get('s') || '';
	state.category = (params.get('category') || '').split(',').filter(Boolean);
	state.min_price = params.get('min_price') || '';
	state.max_price = params.get('max_price') || '';
	state.stock_status = params.get('stock_status') || '';
	state.sort = params.get('ch_sort') || 'default';
	state.attributes = {};

	for (const group of (params.get('attributes') || '').split('|').filter(Boolean)) {
		const [taxonomy, terms] = group.split(':');

		if (taxonomy && terms) {
			state.attributes[taxonomy] = terms.split(',').filter(Boolean);
		}
	}

	return state;
}

/**
 * How many filters a state applies — the same tally PHP prints into the
 * toolbar button, kept in step after every swap.
 */
function countActive(state) {
	return (state.search ? 1 : 0)
		+ state.category.length
		+ Object.values(state.attributes).reduce((total, terms) => total + terms.length, 0)
		+ (state.min_price !== '' ? 1 : 0)
		+ (state.max_price !== '' ? 1 : 0)
		+ (state.stock_status ? 1 : 0);
}

export default function initShopFilters() {
	const layout = document.querySelector('[data-ch-shop]');
	const form = document.querySelector('[data-ch-shop-filter]');

	if (!layout || !form) {
		return;
	}

	const results = layout.querySelector('[data-ch-shop-results]');
	const chipsRegion = layout.querySelector('[data-ch-shop-chips]');
	const count = layout.querySelector('[data-ch-shop-count]');
	const toggle = layout.querySelector('[data-ch-shop-toggle]');
	const badge = layout.querySelector('[data-ch-filter-count]');

	const perPage = parseInt(layout.dataset.perPage || '12', 10);
	const basePath = layout.dataset.base || window.location.pathname;
	const basePathClean = basePath.replace(/page\/\d+\/$/, '');

	let page = pageFromLocation();
	let inFlight = null;

	function setDrawer(open) {
		layout.classList.toggle('filters-open', open);

		if (toggle) {
			toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
		}

		if (open) {
			claimOverlay('shop-filters');
		} else {
			releaseOverlay('shop-filters');
		}
	}

	onDismiss(() => setDrawer(false));

	toggle?.addEventListener('click', () => setDrawer(!layout.classList.contains('filters-open')));

	/**
	 * Fetch the swapped regions for a state and paint them.
	 *
	 * @param {Object} state    Filter state.
	 * @param {number} nextPage Page to show.
	 * @param {string} syncUrl  'push' a history entry, 'replace' the current
	 *                          one, or 'none' to leave the address bar alone.
	 */
	async function apply(state, nextPage, syncUrl = 'push') {
		page = nextPage;

		if (syncUrl !== 'none') {
			const url = toUrl(state, page, basePathClean);

			if (url !== window.location.pathname + window.location.search) {
				// Every filtered view becomes a history entry of its own, so the
				// back button walks the shopper back through their filters;
				// popstate-driven repaints replace instead, never stacking.
				history[syncUrl === 'push' ? 'pushState' : 'replaceState'](null, '', url);
			}
		}

		// On a phone the drawer covers the grid, so a filter chosen inside it
		// closes it to reveal the results it just produced.
		if (layout.classList.contains('filters-open')) {
			setDrawer(false);
		}

		if (badge) {
			const active = countActive(state);

			badge.textContent = String(active);
			badge.hidden = active === 0;
		}

		layout.classList.add('is-loading');

		inFlight?.abort();
		inFlight = new AbortController();

		try {
			const response = await fetch(
				`${settings.restUrl}shop-render?${toApiParams(state, page, perPage, basePathClean)}`,
				{ signal: inFlight.signal, credentials: 'same-origin' }
			);
			const data = await response.json();

			if (!response.ok || !data) {
				return;
			}

			if (results) {
				results.innerHTML = data.results
					? data.results + (data.pagination || '')
					: `<p class="ch-shop__empty">${results.dataset.empty || ''}</p>`;
			}

			if (chipsRegion) {
				chipsRegion.innerHTML = data.chips || '';
			}

			if (count) {
				count.textContent = `${data.count} ${count.dataset.suffix || ''}`.trim();
			}
		} catch (error) {
			// An aborted request is just a newer one arriving; anything else
			// leaves the last good results on screen.
			if (error?.name !== 'AbortError') {
				console.error(error);
			}
		} finally {
			layout.classList.remove('is-loading');
		}
	}

	function applyCurrent(nextPage = 1) {
		apply(readState(form), nextPage);
	}

	// --- Controls ------------------------------------------------------------

	let debounce = null;

	form.addEventListener('input', (event) => {
		const field = event.target;

		if (!(field instanceof HTMLElement)) {
			return;
		}

		// A facet's own search box narrows the list of options in the sidebar; it
		// is not one of the filters the shop is asked for.
		if (field.matches('[data-ch-facet-search]')) {
			return;
		}

		// Free text and price boxes wait for a pause; ticks apply at once.
		const wait = field.type === 'search' || field.type === 'text' ? 300
			: field.type === 'number' ? 500
			: 0;

		clearTimeout(debounce);

		if (wait) {
			debounce = setTimeout(() => applyCurrent(1), wait);
		} else {
			applyCurrent(1);
		}
	});

	// The sort select belongs to the form through its `form` attribute but is
	// not a DOM descendant of it, so its change event never bubbles through
	// the form — the layout wraps both.
	layout.addEventListener('change', (event) => {
		if (event.target instanceof HTMLSelectElement) {
			applyCurrent(1);
		}
	});

	form.addEventListener('submit', (event) => {
		// Only reachable with JavaScript — the noscript button is not rendered
		// into this page at all without it.
		event.preventDefault();
		clearTimeout(debounce);
		applyCurrent(1);
	});

	// --- Long facets ----------------------------------------------------------

	// A catalogue of eighty aisles is more than anyone scrolls through, so those
	// groups are printed with a search box. Every option is already in the page:
	// this only hides the ones that do not match. Ticked options stay put whatever
	// is typed, so a filter already applied is never searched out of sight.
	form.querySelectorAll('[data-ch-facet-search]').forEach((input) => {
		const group = input.closest('.ch-shop-filters__group');
		const list = group?.querySelector('[data-ch-facet-list]');

		if (!list) {
			return;
		}

		const options = Array.from(list.querySelectorAll('[data-ch-facet-option]'));
		const none = list.querySelector('[data-ch-facet-none]');

		input.addEventListener('input', () => {
			const needle = input.value.trim().toLowerCase();
			let shown = 0;

			options.forEach((option) => {
				const match = needle === ''
					|| option.dataset.chFacetName.includes(needle)
					|| option.querySelector('input')?.checked;

				option.hidden = !match;

				if (match) {
					shown += 1;
				}
			});

			if (none) {
				none.hidden = shown > 0;
			}

			list.scrollTop = 0;
		});

		input.addEventListener('keydown', (event) => {
			// Enter would submit the sidebar; here it belongs to the search box.
			if (event.key === 'Enter') {
				event.preventDefault();

				return;
			}

			// Escape empties the box rather than closing the drawer around it.
			if (event.key === 'Escape' && input.value !== '') {
				event.stopPropagation();
				input.value = '';
				input.dispatchEvent(new Event('input', { bubbles: true }));
			}
		});
	});

	// --- Chips ----------------------------------------------------------------

	document.addEventListener('click', (event) => {
		if (!(event.target instanceof Element)) {
			return;
		}

		const remove = event.target.closest('[data-ch-filter-remove]');

		if (remove) {
			const state = readState(form);
			const [kind, ...rest] = remove.dataset.chFilterRemove.split(':');
			const value = rest.join(':');

			if (kind === 'search') {
				state.search = '';
			} else if (kind === 'category') {
				state.category = state.category.filter((slug) => slug !== value);
			} else if (kind === 'attribute') {
				const [taxonomy, slug] = [value.slice(0, value.lastIndexOf(':')), value.slice(value.lastIndexOf(':') + 1)];

				state.attributes[taxonomy] = (state.attributes[taxonomy] || []).filter((term) => term !== slug);
			} else if (kind === 'price') {
				state[value === 'min' ? 'min_price' : 'max_price'] = '';
			} else if (kind === 'stock') {
				state.stock_status = '';
			}

			writeState(form, state);
			applyCurrent(1);

			return;
		}

		if (event.target.closest('[data-ch-filter-clear]')) {
			const state = readState(form);

			writeState(form, {
				...state,
				search: '',
				category: [],
				min_price: '',
				max_price: '',
				attributes: {},
				stock_status: '',
			});

			applyCurrent(1);

			return;
		}

		// --- Pagination --------------------------------------------------------

		const link = event.target.closest('[data-ch-shop-results] .ch-pager a');

		if (!link) {
			return;
		}

		event.preventDefault();

		const url = new URL(link.href, window.location.origin);
		const inPath = url.pathname.match(/\/page\/(\d+)\/?$/);
		const next = inPath
			? parseInt(inPath[1], 10)
			: parseInt(url.searchParams.get('ch_page') || url.searchParams.get('paged') || '1', 10);

		apply(readState(form), Math.max(1, next));

		window.scrollTo({ top: layout.offsetTop, behavior: 'smooth' });
	});

	// --- Back and forward -------------------------------------------------------

	window.addEventListener('popstate', () => {
		const state = stateFromLocation(form);

		writeState(form, state);
		apply(state, pageFromLocation(), 'replace');
	});
}
